import { Plus, X } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { SummaryCard } from '@nucleus-commerce/admin-kit/forms';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import { Textarea } from '@nucleus-commerce/ui/components/textarea';

import type { MetadataEntryForm } from '@/shared/forms/metadata-entry-schema';

/** The slice of the host form these fields bind to; products and store products both name it `metadata`. */
interface MetadataFormSlice {
  metadata: MetadataEntryForm[];
}

export interface MetadataFieldsProps {
  description?: string;
  /** Beside the title: the store page's This store chip. */
  badge?: ReactNode;
}

/**
 * Free-form key / value pairs for tenant integrations. A value the API holds as a string is a
 * plain input; anything else is edited as JSON so a round trip keeps its type. Collapsed until the
 * user opens it; a failed save with an error inside opens it so nothing invalid stays hidden. It
 * ignores the read-only scope on purpose: on a store's page these are that store's own metadata.
 */
export function MetadataFields({ description, badge }: MetadataFieldsProps) {
  const { t } = useTranslation('products');
  const form = useFormContext<MetadataFormSlice>();
  const { fields, append, remove } = useFieldArray({ control: form.control, name: 'metadata' });
  const hasError = form.formState.errors.metadata != null;
  const readOnly = form.formState.disabled;
  const [open, setOpen] = useState(false);
  const [openedForError, setOpenedForError] = useState(false);
  if (hasError !== openedForError) {
    setOpenedForError(hasError);
    if (hasError) setOpen(true);
  }

  return (
    <SummaryCard
      title={t('metadata.title')}
      badge={badge}
      meta={
        fields.length === 0
          ? t('rail.metadata.none')
          : t('rail.metadata.count', { count: fields.length })
      }
      collapsible
      open={open}
      onOpenChange={setOpen}
    >
      <p className="text-muted-foreground text-[13px]">
        {description ?? t('metadata.description')}
      </p>
      {fields.length === 0 ? (
        <p className="text-muted-foreground text-[13px]">{t('rail.metadata.empty')}</p>
      ) : (
        <ul className="flex flex-col gap-3" aria-label={t('metadata.title')}>
          {fields.map((entry, index) => (
            <li
              key={entry.id}
              className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,2fr)_auto] sm:items-start"
            >
              <FormField
                control={form.control}
                name={`metadata.${index}.key`}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="sr-only">{t('metadata.key')}</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        className="font-mono text-[13px]"
                        placeholder={t('metadata.keyPlaceholder')}
                        aria-label={t('metadata.keyFor', { n: index + 1 })}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name={`metadata.${index}.value`}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="sr-only">{t('metadata.value')}</FormLabel>
                    <FormControl>
                      {entry.json ? (
                        <Textarea
                          {...field}
                          rows={2}
                          className="font-mono text-[13px]"
                          aria-label={t('metadata.jsonFor', { n: index + 1 })}
                        />
                      ) : (
                        <Input
                          {...field}
                          placeholder={t('metadata.valuePlaceholder')}
                          aria-label={t('metadata.valueFor', { n: index + 1 })}
                        />
                      )}
                    </FormControl>
                    {entry.json ? (
                      <p className="text-muted-foreground text-xs">{t('metadata.jsonHint')}</p>
                    ) : null}
                    <FormMessage />
                  </FormItem>
                )}
              />
              {readOnly ? null : (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  className="text-muted-foreground"
                  aria-label={t('metadata.remove', { n: index + 1 })}
                  onClick={() => remove(index)}
                >
                  <X aria-hidden />
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
      {readOnly ? null : (
        <div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => append({ key: '', value: '', json: false })}
          >
            <Plus aria-hidden />
            {t('rail.metadata.add')}
          </Button>
        </div>
      )}
    </SummaryCard>
  );
}
